import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { filter, firstValueFrom, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { NAVIGATION_ITEMS } from './core/constants/app-navigation.constants';
//import { environment } from '../environments/environment.dev';
import { CalAngularService } from '@cvx/cal-angular';
import { SessionStorageService } from './core/services/app-session.service';
import { UserDetails } from './core/services/app-user.service';
import { PermissionService } from './core/services/permission.service';
import { isPlatformBrowser } from '@angular/common';
import { NavigationItem, FooterComponent, HeaderComponent } from '@cvx/nextpage';
import { AppConfig } from './core/services/app-config.service';
import { NavigationComponent } from './layout/navigation/navigation';
import { Spinner } from './layout/spinner/spinner';
import { NotificationBanner } from './shared/components/notification-banner/notification-banner';

@Component({
  selector: 'app-root',
  imports: [
    FooterComponent,
    RouterOutlet,
    NavigationComponent,
    HeaderComponent,
    Spinner,
    NotificationBanner,
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('toggleMenu') toggleMenu!: ElementRef;
  @ViewChild('search') search!: ElementRef;
  @ViewChild('toggleSearch') toggleSearch!: ElementRef;
  siteName = 'Logistics Control Tower';
  welcomeMessage = signal('');
  /** Filtered per the logged-in user's module permissions; empty until permissions are loaded. */
  navigationItems = computed<NavigationItem[]>(() =>
    this.permissionService.user()
      ? this.permissionService.filterNavigationItems(NAVIGATION_ITEMS)
      : [],
  );
  /** Whether to hide the header and navigation (standalone/new window mode). */
  isStandaloneMode = signal(false);

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly router: Router,
    private readonly session: SessionStorageService,
    private readonly calService: CalAngularService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Detect standalone mode from query parameter
    const standalone = new URLSearchParams(window.location.search).get('standalone');
    this.isStandaloneMode.set(standalone === 'true');

    // Apply fluid layout class if enabled in config
    try {
      const isFluid = AppConfig.settings?.fluidLayout;
      if (isFluid && typeof document !== 'undefined') {
        document.body.classList.add('fluid-layout');
      } else if (typeof document !== 'undefined') {
        document.body.classList.remove('fluid-layout');
      }
    } catch (e) {
      // ignore if config not available
    }

    // isUserSignedIn() is a one-shot check that runs before login completes; calGuard
    // performs the sign-in during route activation, so populate the profile once a
    // guarded route has finished loading (user is guaranteed signed in by then).
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(async () => {
        const signedIn = await firstValueFrom(this.calService.isUserSignedIn());
        if (!signedIn) {
          return;
        }
        this.permissionService.startSynchronization();
        if (!this.welcomeMessage()) {
          this.setUserContext(await this.calService.getClaims());
        }
      });
  }

  async logIn() {
    const data = await lastValueFrom(this.calService.userInitiatedSignIn());
    this.setUserContext(data);
  }

  private setUserContext(data: Awaited<ReturnType<CalAngularService['getClaims']>>): void {
    const user: UserDetails[] = [];
    const name = data?.name ?? '';
    if (!name) {
      return;
    }

    let fullName = name.replace(/\s*\[[^\]]*$/, '');
    fullName = fullName.replace(/\s*,\s*/, ' ');
    const fullName1 = fullName.split(' ');
    const fs = fullName1[1] + ' ' + fullName1[0];
    user.push({
      firstName: fullName1[1],
      lastName: fullName1[0],
      emailId: data.email,
      userId: data.objectId,
      fullName: fs,
      roles: data.roles,
      isUser: data.isUser,
      cai: data.logOnName,
    });
    this.welcomeMessage.set(`Hi, ${fullName1[1]}`);
    this.session.setItem('currentUser', structuredClone(user));
  }

  logout() {
    this.calService.userInitiatedSignOut().subscribe((value: boolean) => {
      this.permissionService.clearUser();
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      const login = document.getElementById('login');
      const logout = document.querySelector('.logout');

      if (login && logout) {
        login.appendChild(logout);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setupRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }
      });
  }
}
